"use client"

import { Textarea } from "@/components/ui/textarea"
import { useState, useMemo, useEffect, useRef, useCallback } from "react"
import { useSearchParams } from "next/navigation"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Building2,
  Eye,
  EyeOff,
  ChevronsUpDown,
  Loader2,
  Check,
  UserCircle,
  FileCheck2,
} from "lucide-react"
import { useAuth } from "@/hooks/use-auth"
import { useCurrentTenantQuery } from "@/hooks/queries/current-tenant"
import { usePermissions } from "@/hooks/use-permissions"
import type { UiLocale } from "@/lib/i18n/locales"
import { useUpdateMyProfile } from "@/hooks/mutations/user-profile"
import type React from "react"
import { cn } from "@/lib/utils"
import {
  buildTimezoneOptions,
  withCustomTimezone,
  type TimezoneOption,
} from "@/lib/utils/timezones"
import { OrganizationTab } from "@/components/organization/organization-tab"
import { AiDisclosureReport } from "@/components/organization/ai-disclosure-report"
import { TENANTS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { UiLanguageForm } from "./ui-language-form"
import { useTranslations } from "next-intl"

type ProfileTab = "profile" | "organization" | "compliance-reports"

export function UserProfilePage() {
  const tUi = useTranslations("Feedback.UserProfilePage")

  const t = useTranslations("UserProfile")
  const common = useTranslations("Common")
  const { user } = useAuth()
  const { tenant } = useCurrentTenantQuery()
  const { can } = usePermissions()
  const updateProfileMutation = useUpdateMyProfile()
  const searchParams = useSearchParams()

  // Organization settings and reports require tenants.view_own, including deep links.
  const canViewOrg = can(TENANTS_PERMISSIONS.VIEW_OWN)

  const tabParam = searchParams.get("tab")
  const initialTab = resolveSettingsTab(tabParam, canViewOrg)
  const [activeTab, setActiveTab] = useState<ProfileTab>(initialTab)
  const visibleTab = resolveSettingsTab(activeTab, canViewOrg)

  const handleTabChange = useCallback((value: string) => {
    const tab = resolveSettingsTab(value, canViewOrg)
    setActiveTab(tab)
    if (typeof window === "undefined") return
    const url = new URL(window.location.href)
    if (tab === "profile") {
      url.searchParams.delete("tab")
    } else {
      url.searchParams.set("tab", tab)
    }
    window.history.replaceState({}, "", url.toString())
  }, [canViewOrg])

  const [formData, setFormData] = useState({
    name: "",
    password: "",
    confirmPassword: "",
    phone: "",
    website: "",
    description: "",
    timezone: "UTC",
  })

  // Load user data into form only on initial mount (not after refreshAuth)
  const formInitialized = useRef(false)
  useEffect(() => {
    if (user && !formInitialized.current) {
      formInitialized.current = true
      setFormData({
        name: user.name || "",
        password: "",
        confirmPassword: "",
        phone: String(user.phone || ""),
        website: String(user.website || ""),
        description: String(user.description || ""),
        timezone: user.timezone || "UTC",
      })
    }
  }, [user])

  const baseTimezoneOptions = useMemo<TimezoneOption[]>(() => buildTimezoneOptions(), [])
  const timezoneOptions = useMemo(
    () => withCustomTimezone(baseTimezoneOptions, formData.timezone),
    [baseTimezoneOptions, formData.timezone],
  )
  const [isTimezoneOpen, setIsTimezoneOpen] = useState(false)
  const [timezoneSearch, setTimezoneSearch] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [status, setStatus] = useState<({ type: "success" | "error" } & ({ message: string } | { key: "profileSaved" | "passwordMismatch" | "passwordTooShort" })) | null>(null)

  const filteredTimezoneOptions = useMemo(() => {
    const query = timezoneSearch.trim().toLowerCase()
    if (!query) {
      return timezoneOptions
    }
    return timezoneOptions.filter((option) => option.searchText.includes(query))
  }, [timezoneOptions, timezoneSearch])

  const selectedTimezoneOption = useMemo(
    () => timezoneOptions.find((option) => option.value === formData.timezone),
    [timezoneOptions, formData.timezone],
  )

  const handleUpdateProfile = async (locale?: UiLocale): Promise<boolean> => {
    setStatus(null)

    if (formData.password && formData.password !== formData.confirmPassword) {
      setStatus({ type: "error", key: "passwordMismatch" })
      return false
    }

    if (formData.password && formData.password.length > 0 && formData.password.length < 6) {
      setStatus({ type: "error", key: "passwordTooShort" })
      return false
    }

    if (!user) return false

    try {
      await updateProfileMutation.mutateAsync({
        name: formData.name, phone: formData.phone, website: formData.website,
        description: formData.description, timezone: formData.timezone,
        password: formData.password, ...(locale ? { locale } : {}),
      })

      setStatus({ type: "success", key: "profileSaved" })
      // Keep current form values; only clear password fields
      setFormData((prev) => ({ ...prev, password: "", confirmPassword: "" }))
      return true
    } catch (error: unknown) {
      setStatus({ type: "error", message: error instanceof Error ? error.message : t("profileFailed") })
      return false
    }
  }

  let statusMessage: string | null = null
  if (status) statusMessage = "key" in status ? t(status.key) : status.message

  const timezoneField = (
            <div className="max-w-xl">
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label htmlFor="timezone"><span key={t("timezone")}>{t("timezone")}</span></Label>
                  {tenant?.timezone && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto max-w-full whitespace-normal p-0 text-xs leading-none"
                      disabled={tenant.timezone === formData.timezone}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          timezone: tenant.timezone ?? prev.timezone,
                        }))
                      }
                      title={t("organizationTimezoneHint")}
                    >
                      <span key={tUi("useOrganizationTimezone")}>{tUi("useOrganizationTimezone")}</span></Button>
                  )}
                </div>
                <Popover open={isTimezoneOpen} onOpenChange={(open) => setIsTimezoneOpen(open)}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      id="timezone"
                      aria-haspopup="listbox"
                      aria-expanded={isTimezoneOpen}
                      className={cn(
                        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-left text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                        !selectedTimezoneOption && "text-muted-foreground",
                      )}
                    >
                      <span key={selectedTimezoneOption ? selectedTimezoneOption.label : t("selectTimezone")} className="line-clamp-1">
                        {selectedTimezoneOption ? selectedTimezoneOption.label : t("selectTimezone")}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
                    <Command shouldFilter={false}>
                      <div className="border-b border-border bg-popover">
                        <CommandInput
                          autoFocus
                          value={timezoneSearch}
                          onValueChange={setTimezoneSearch}
                          placeholder={t("searchTimezone")}
                        />
                      </div>
                      <CommandList className="max-h-64 overflow-y-auto">
                        {filteredTimezoneOptions.length > 0 ? (
                          <CommandGroup className="p-0">
                            {filteredTimezoneOptions.map((option) => (
                              <CommandItem
                                key={option.value}
                                value={option.value}
                                onSelect={() => {
                                  setFormData((prev) => ({ ...prev, timezone: option.value }))
                                  setTimezoneSearch("")
                                  setIsTimezoneOpen(false)
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    option.value === formData.timezone ? "opacity-100" : "opacity-0",
                                  )}
                                />
                                <span className="line-clamp-2">{option.label}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        ) : (
                          <div className="px-3 py-4 text-sm text-muted-foreground"><span key={t("noTimezones")}>{t("noTimezones")}</span></div>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  <span key={t("seasonalChanges")}>{t("seasonalChanges")}</span>
                </p>
              </div>
            </div>

  )

  if (!user) {
    return (
      <div className="p-6 max-w-4xl mx-auto flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <p><span key={t("loading")}>{t("loading")}</span></p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <Tabs value={visibleTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="min-h-10 h-auto flex flex-wrap justify-start [&>button]:min-h-10">
          <TabsTrigger value="profile">
            <UserCircle className="w-4 h-4 mr-2" />
            <span><span key={t("title")}>{t("title")}</span></span>
          </TabsTrigger>
          {canViewOrg && (
            <TabsTrigger value="organization">
              <Building2 className="w-4 h-4 mr-2" />
              <span><span key={t("organization")}>{t("organization")}</span></span>
            </TabsTrigger>
          )}
          {canViewOrg && (
            <TabsTrigger value="compliance-reports">
              <FileCheck2 className="w-4 h-4 mr-2" />
              <span><span key={t("complianceReports")}>{t("complianceReports")}</span></span>
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="profile" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl"><span key={t("title")}>{t("title")}</span></CardTitle>
          <CardDescription><span key={t("description")}>{t("description")}</span></CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <UiLanguageForm onSaveProfile={handleUpdateProfile} timezoneField={timezoneField}>
            {(isSubmitting) => <>
            {status && (
              <Alert
                className={status.type === "success" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}
              >
                <AlertDescription className={status.type === "success" ? "text-green-800" : "text-red-800"}>
                  <span key={statusMessage}>{statusMessage}</span>
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name"><span key={t("fullName")}>{t("fullName")}</span></Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  autoComplete="name"
                />
              </div>
              <div>
                <Label htmlFor="email"><span key={t("accountEmail")}>{t("accountEmail")}</span></Label>
                <Input id="email" type="email" value={user.email} disabled autoComplete="email" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="password"><span key={t("newPassword")}>{t("newPassword")}</span></Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={t("keepPassword")}
                    autoComplete="new-password"
                    className="pr-12"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="confirmPassword"><span key={t("confirmPassword")}>{t("confirmPassword")}</span></Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    autoComplete="new-password"
                    className="pr-12"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="phone"><span key={t("phone")}>{t("phone")}</span></Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  autoComplete="tel"
                />
              </div>
              <div>
                <Label htmlFor="website"><span key={t("website")}>{t("website")}</span></Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://example.com"
                  autoComplete="url"
                />
              </div>
            </div>


            <div>
              <Label htmlFor="description"><span key={t("profileDescription")}>{t("profileDescription")}</span></Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                placeholder={t("aboutYou")}
              />
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <span key={[isSubmitting ? "saving" : "idle", isSubmitting ? common("saving") : common("save")].join(":")}>
                  {isSubmitting ? common("saving") : common("save")}
                </span>
              </Button>
            </div>
            </>}
          </UiLanguageForm>
        </CardContent>
      </Card>
        </TabsContent>
        {canViewOrg && (
          <TabsContent value="compliance-reports" className="space-y-6">
            <AiDisclosureReport />
          </TabsContent>
        )}
        {canViewOrg && (
          <TabsContent value="organization" className="space-y-6">
            <OrganizationTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}

function resolveSettingsTab(value: string | null, canViewOrganization: boolean): ProfileTab {
  switch (value) {
    case "organization":
    case "compliance-reports":
      return canViewOrganization ? value : "profile"
    default:
      return "profile"
  }
}
